import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { copilotUrlTransform } from "@sill/shared/lib/safe-markdown-url";

// On-brand markdown styling for the copilot's LLM output — a pro-token port of
// the consumer's `AdvisorMarkdown`. The advisor produces a constrained palette
// (headings, lists, tables, inline code, emphasis, rules), so these rules keep
// the bubble legible without dragging in `@tailwindcss/typography`. Pro DS
// tokens only (ADR-034): `text-fg`, `text-muted-foreground`, `bg-muted`,
// `border-border`.
const components: Components = {
  p: ({ children }) => (
    <p className="text-sm leading-6 text-fg [&:not(:first-child)]:mt-2">
      {children}
    </p>
  ),
  h1: ({ children }) => (
    <h1 className="mt-3 text-base font-semibold text-fg first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-3 text-[15px] font-semibold text-fg first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-3 text-sm font-semibold text-fg first:mt-0">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="mt-2 text-sm font-semibold text-fg first:mt-0">
      {children}
    </h4>
  ),
  ul: ({ children }) => (
    <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm leading-6 text-fg marker:text-muted-foreground">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mt-1.5 list-decimal space-y-1 pl-5 text-sm leading-6 text-fg marker:text-muted-foreground">
      {children}
    </ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => (
    <strong className="font-semibold text-fg">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  // Never render an image (SEC-03): a browser fetches <img src> with no
  // click, so a model steered by buyer-typed text in a tool result could
  // otherwise beacon the pro's book to any host. The alt text is dropped too.
  img: () => null,
  // A link whose href the urlTransform dropped renders as plain text.
  a: ({ href, children }) =>
    href ? (
      <a
        className="text-primary underline underline-offset-2 hover:opacity-80"
        href={href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {children}
      </a>
    ) : (
      <>{children}</>
    ),
  code: ({ children, className: codeClass }) => {
    const isBlock = (codeClass ?? "").includes("language-");
    if (isBlock) {
      return (
        <code className="block whitespace-pre rounded-lg border border-border bg-muted px-3 py-2 font-mono text-[13px] leading-5 text-fg">
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-muted px-1 py-0.5 font-mono text-[13px] text-fg">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="mt-2 overflow-x-auto rounded-lg border border-border bg-muted p-3 text-[13px] leading-5">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mt-2 border-l-2 border-border pl-3 text-sm leading-6 text-muted-foreground">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-border" />,
  table: ({ children }) => (
    <div className="mt-2.5 overflow-x-auto rounded-xl border border-border bg-card">
      <table className="w-full border-collapse text-[13px]">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-border bg-muted px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-fg-muted">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-border/60 px-3 py-2 align-top text-fg last:[&>*]:mb-0">
      {children}
    </td>
  ),
};

/**
 * Renders assistant markdown inside the copilot bubble. User messages stay
 * plain text (they're literal input) — only advisor output flows through here.
 *
 * SEC-03: no images ever, and links only to relative URLs or the first-party /
 * reference hosts in `@sill/shared/lib/safe-markdown-url` (the same list the
 * backend mdsafe pass keeps). Raw HTML is not enabled (no rehype-raw), so
 * markup in the model's text renders as text.
 */
export function ProMarkdown({ children }: { children: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={components}
      urlTransform={copilotUrlTransform}
    >
      {children}
    </ReactMarkdown>
  );
}
